import { motion } from 'framer-motion';
import { 
  Target, 
  Users, 
  MessageSquare, 
  BarChart3,
  ArrowRight,
  Search,
  UserPlus,
  Eye,
  Send,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const steps = [
  {
    icon: Search,
    title: 'Recherche de cible',
    description: 'Une assistante spécialisée recherche des entreprises qui peuvent devenir tes clients : agences immobilières, entreprises du bâtiment, architectes, promoteurs, syndics, entreprises locales.',
  },
  {
    icon: UserPlus,
    title: 'Création du fichier prospect',
    description: 'Chaque entreprise est analysée. Nous récupérons les coordonnées, les responsables, leur présence sur LinkedIn et leurs contacts professionnels.',
  },
  {
    icon: Eye,
    title: 'Interaction et visibilité',
    description: 'Une deuxième assistante suit ces entreprises sur LinkedIn, interagit avec leurs publications, commente et crée de la visibilité autour de ton profil.',
  },
  {
    icon: Send,
    title: 'Messages personnalisés',
    description: 'Le système envoie des messages personnalisés pour se présenter, engager la discussion et proposer un échange. Pas de spam, une relation naturelle.',
  },
];

const features = [
  {
    icon: Target,
    title: 'Prospection ciblée',
    description: 'Identification précise des entreprises qui correspondent à ta cible commerciale.',
  },
  {
    icon: Users,
    title: 'Deux assistantes dédiées',
    description: 'Une pour la recherche, une pour les interactions et le suivi.',
  },
  {
    icon: MessageSquare,
    title: 'Engagement naturel',
    description: 'Création de relations authentiques, pas de messages automatisés impersonnels.',
  },
  {
    icon: BarChart3,
    title: 'SaaS de pilotage',
    description: 'Un outil pour suivre les prospects, les interactions et les opportunités.',
  },
];

export default function NewClientMachinePage() {
  return (
    <div data-testid="new-client-machine-page">
      {/* Hero */}
      <section className="py-20 md:py-32 gradient-mesh">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-rm-secondary/10 text-rm-secondary text-sm font-medium rounded-full mb-4">
              Machine à Nouveaux Clients
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-rm-text mb-6">
              Un système pour trouver de nouveaux clients pendant que tu travailles
            </h1>
            <p className="text-lg md:text-xl text-rm-text-secondary mb-8">
              Beaucoup d'entrepreneurs savent très bien faire leur métier. Mais ils manquent de temps 
              pour trouver de nouveaux clients. La Machine à nouveaux clients automatise ce travail.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-rm-secondary hover:bg-rm-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all group">
                Découvrir le système
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-rm-text mb-6">
              Comment ça fonctionne
            </h2>
            <p className="text-lg text-rm-text-secondary max-w-3xl mx-auto">
              Un système complet de prospection et de visibilité, géré par tes assistantes 
              et les agents IA.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-slate-50 rounded-2xl p-8 relative"
                >
                  <div className="absolute -top-4 -left-4 w-10 h-10 bg-rm-primary text-white rounded-xl flex items-center justify-center text-lg font-bold shadow-lg">
                    {index + 1}
                  </div>
                  <div className="w-14 h-14 bg-rm-accent/20 rounded-xl flex items-center justify-center mb-4 mt-2">
                    <Icon className="w-7 h-7 text-rm-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-rm-text mb-3">{step.title}</h3>
                  <p className="text-rm-text-secondary leading-relaxed">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-all text-center"
                >
                  <div className="w-14 h-14 bg-rm-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-rm-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-rm-text mb-2">{feature.title}</h3>
                  <p className="text-rm-text-secondary text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Result */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-rm-accent/10">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-16 h-16 text-rm-primary mx-auto mb-6" />
            <h2 className="text-3xl md:text-5xl font-bold text-rm-text mb-6">
              Créer des opportunités commerciales
            </h2>
            <p className="text-lg md:text-xl text-rm-text-secondary mb-8 leading-relaxed">
              L'objectif n'est pas d'envoyer des milliers de messages. L'objectif est de créer 
              de la visibilité, engager des conversations et générer des opportunités commerciales. 
              Pendant que tu es sur tes chantiers, le système travaille pour toi.
            </p>
            <div className="bg-white rounded-2xl p-8 mb-8 border border-slate-200 inline-block">
              <div className="text-4xl font-bold text-rm-primary mb-2">1499€/mois</div>
              <p className="text-rm-text-secondary">
                Machine à nouveaux clients incluse dans l'offre Croissance
              </p>
            </div>
            <div className="block">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-rm-secondary hover:bg-rm-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all group">
                  Réserver un entretien
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final message */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-rm-text mb-6">
              Continuez à faire ton métier.{' '}
              <span className="text-gradient">Nous nous occupons de trouver de nouvelles opportunités.</span>
            </h2>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
